/**
 * Toaster store — push success / error / info notifications from anywhere
 * (services, components) without prop-drilling. Mounted once at the app
 * shell via the ``<Toaster />`` component which subscribes to this store.
 *
 * Replaces ``window.alert`` (ADR-048).
 */

import { create } from 'zustand';

export type ToastKind = 'success' | 'error' | 'info';

export type Toast = {
  id: string;
  kind: ToastKind;
  message: string;
  /** Auto-dismiss after this many ms (default 4000 for info/success, 6000 for error). */
  durationMs: number;
};

type ToasterState = {
  toasts: Toast[];
  push: (kind: ToastKind, message: string, durationMs?: number) => string;
  dismiss: (id: string) => void;
  clear: () => void;
};

let counter = 0;
const nextId = () => `t-${Date.now()}-${++counter}`;

export const useToasterStore = create<ToasterState>((set) => ({
  toasts: [],
  push: (kind, message, durationMs) => {
    const id = nextId();
    const ttl = durationMs ?? (kind === 'error' ? 6000 : 4000);
    set((s) => ({
      toasts: [...s.toasts, { id, kind, message, durationMs: ttl }],
    }));
    if (ttl > 0) {
      setTimeout(() => {
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
      }, ttl);
    }
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

/**
 * Convenience helpers for callers that don't need the store directly.
 * Usage : ``toast.error("erreur réseau")``.
 */
export const toast = {
  success: (msg: string, ttl?: number) => useToasterStore.getState().push('success', msg, ttl),
  error: (msg: string, ttl?: number) => useToasterStore.getState().push('error', msg, ttl),
  info: (msg: string, ttl?: number) => useToasterStore.getState().push('info', msg, ttl),
};
